import { CSSProperties, useState, useEffect } from 'react';
import { AvatarFrame, AvatarShape } from '@/types/editor';

export const AVATAR_FRAME_GRADIENTS: Partial<Record<AvatarFrame, string>> = {
  'gradient':           'linear-gradient(135deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
  'gradient-sunset':    'linear-gradient(135deg, #FF6B35 0%, #FF9A44 35%, #E05C97 100%)',
  'gradient-ocean':     'linear-gradient(135deg, #0077B6 0%, #00B4D8 55%, #90E0EF 100%)',
  'gradient-gold':      'linear-gradient(135deg, #BF953F 0%, #FCF6BA 30%, #B38728 50%, #FBF5B7 70%, #AA771C 100%)',
  'gradient-galaxy':    'linear-gradient(135deg, #4158D0 0%, #C850C0 46%, #FFCC70 100%)',
  'gradient-forest':    'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
  'gradient-aurora':    'linear-gradient(135deg, #43e97b 0%, #38f9d7 30%, #4facfe 65%, #a18cd1 100%)',
  'gradient-rose':      'linear-gradient(135deg, #f43f5e 0%, #ec4899 50%, #8b5cf6 100%)',
  'gradient-fire':      'linear-gradient(135deg, #f83600 0%, #ff6b35 40%, #f9d423 100%)',
  'gradient-candy':     'linear-gradient(135deg, #a8edea 0%, #c9b3ff 50%, #fed6e3 100%)',
  'gradient-mint':      'linear-gradient(135deg, #00b09b 0%, #96c93d 100%)',
  'gradient-dusk':      'linear-gradient(135deg, #2c3e50 0%, #6b4c9a 50%, #fd79a8 100%)',
  'gradient-peach':     'linear-gradient(135deg, #ffb347 0%, #ff6b6b 50%, #feca57 100%)',
  'gradient-electric':  'linear-gradient(135deg, #00d2ff 0%, #3a7bd5 100%)',
  'gradient-lemon':     'linear-gradient(135deg, #f7ff00 0%, #db36a4 100%)',
  'gradient-berry':     'linear-gradient(135deg, #6a0572 0%, #ab2891 50%, #e8457b 100%)',
  'gradient-nordic':    'linear-gradient(135deg, #1a1a2e 0%, #16213e 40%, #0f3460 70%, #533483 100%)',
  'gradient-mango':     'linear-gradient(135deg, #f46b45 0%, #eea849 100%)',
};

/** Frames whose color is fixed (preset gradient) and cannot be customized */
export const FIXED_GRADIENT_FRAMES: AvatarFrame[] = [
  'gradient', 'gradient-sunset', 'gradient-ocean', 'gradient-gold',
  'gradient-galaxy', 'gradient-forest', 'gradient-aurora', 'gradient-rose',
  'gradient-fire', 'gradient-candy',
  'gradient-mint', 'gradient-dusk', 'gradient-peach', 'gradient-electric',
  'gradient-lemon', 'gradient-berry', 'gradient-nordic', 'gradient-mango',
];

/** Clip-path values keyed by AvatarShape */
export const AVATAR_SHAPE_CLIP_PATHS: Partial<Record<AvatarShape, string>> = {
  hexagon:       'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
  diamond:       'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
  pentagon:      'polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)',
  star:          'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
  star6:         'polygon(50% 0%, 67% 25%, 100% 25%, 75% 50%, 100% 75%, 67% 75%, 50% 100%, 33% 75%, 0% 75%, 25% 50%, 0% 25%, 33% 25%)',
  octagon:       'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)',
  shield:        'polygon(50% 0%, 100% 15%, 100% 65%, 50% 100%, 0% 65%, 0% 15%)',
  cross:         'polygon(30% 0%, 70% 0%, 70% 30%, 100% 30%, 100% 70%, 70% 70%, 70% 100%, 30% 100%, 30% 70%, 0% 70%, 0% 30%, 30% 30%)',
  arrow:         'polygon(50% 0%, 100% 50%, 75% 50%, 75% 100%, 25% 100%, 25% 50%, 0% 50%)',
  triangle:      'polygon(50% 0%, 100% 100%, 0% 100%)',
  parallelogram: 'polygon(25% 0%, 100% 0%, 75% 100%, 0% 100%)',
  chevron:       'polygon(0% 0%, 75% 0%, 100% 50%, 75% 100%, 0% 100%, 25% 50%)',
  flag:          'polygon(0% 0%, 75% 0%, 100% 50%, 75% 100%, 0% 100%)',
  bevel:         'polygon(15% 0%, 85% 0%, 100% 15%, 100% 85%, 85% 100%, 15% 100%, 0% 85%, 0% 15%)',
  notch:         'polygon(0% 0%, 100% 0%, 100% 80%, 50% 100%, 0% 80%)',
  rabbet:        'polygon(0% 15%, 15% 0%, 85% 0%, 100% 15%, 100% 85%, 85% 100%, 15% 100%, 0% 85%)',
};

/** @deprecated Use AVATAR_SHAPE_CLIP_PATHS — kept as alias for callers not yet updated */
export const AVATAR_CLIP_PATHS = AVATAR_SHAPE_CLIP_PATHS as Partial<Record<AvatarFrame, string>>;

/** Legacy shape names that were stored in avatar_frame before avatar_shape field existed */
const LEGACY_SHAPE_FRAMES = new Set<string>([
  'square', 'hexagon', 'diamond', 'pentagon',
  'star', 'star6', 'octagon', 'shield',
  'cross', 'arrow', 'triangle',
  'parallelogram', 'chevron', 'flag',
  'bevel', 'notch', 'rabbet',
]);

function shapeClassFor(shape: AvatarShape, hasClipPath: boolean): string {
  if (shape === 'square') return 'rounded-xl';
  if (hasClipPath) return '';
  return 'rounded-full'; // circle
}

function resolveFrameStyle(frame: AvatarFrame, color: string): CSSProperties {
  switch (frame) {
    case 'ring':        return { boxShadow: `0 0 0 3px ${color}` };
    case 'thick':       return { boxShadow: `0 0 0 6px ${color}` };
    case 'glow':        return { boxShadow: `0 0 0 2px ${color}, 0 0 22px 6px ${color}80` };
    case 'neon':        return { boxShadow: `0 0 0 2px ${color}, 0 0 12px 4px ${color}, 0 0 30px 8px ${color}50` };
    case 'soft-glow':   return { boxShadow: `0 0 0 1px ${color}40, 0 0 35px 15px ${color}55` };
    case 'multi-ring':  return { boxShadow: `0 0 0 2px ${color}, 0 0 0 5px ${color}60, 0 0 0 8px ${color}30` };
    case 'pulse-ring':  return { boxShadow: `0 0 0 1.5px ${color}, 0 0 0 5px ${color}35, 0 0 0 9px ${color}18` };
    case 'hard-shadow': return { boxShadow: `8px 8px 0px ${color}` };
    case 'shadow':      return { boxShadow: `6px 6px 0px ${color}` };
    case 'dbl-shadow':  return { boxShadow: `4px 4px 0px ${color}, 8px 8px 0px ${color}50` };
    case 'vignette':    return { boxShadow: `inset 0 0 18px 5px ${color}90` };
    case 'inset-ring':  return { boxShadow: `inset 0 0 0 3px ${color}` };
    case 'border-glow': return { border: `2px solid ${color}`, boxShadow: `0 0 15px 5px ${color}60` };
    case 'retro':       return { border: `3px solid ${color}`, boxShadow: `4px 4px 0 ${color}` };
    case 'photo-frame': return { border: `4px solid ${color}`, outline: `2px solid ${color}`, outlineOffset: '-10px' };
    case 'minimal':     return { border: `1px solid ${color}` };
    case 'outline':     return { outline: `1.5px solid ${color}`, outlineOffset: '3px' };
    case 'double-ring': return { border: `2px solid ${color}`, outline: `2px solid ${color}`, outlineOffset: '4px' };
    case 'dashed':      return { border: `3px dashed ${color}` };
    case 'dots':        return { border: `3px dotted ${color}` };
    case 'groove':      return { border: `4px groove ${color}` };
    case 'ridge':       return { border: `4px ridge ${color}` };
    default:            return {};
  }
}

interface ProfileAvatarProps {
  /** Shape of the avatar container. When omitted, derived from legacy frame value for backward compat. */
  shape?: AvatarShape;
  frame?: AvatarFrame;
  /** Explicit frame color. Falls back to btnBg when empty/omitted. Only applies to non-gradient frames. */
  frameColor?: string;
  avatarUrl?: string | null;
  fallback: string;
  btnBg: string;
  btnText: string;
  /** Extra className applied to the outermost element (e.g. mb-4) */
  className?: string;
}

export function ProfileAvatar({
  shape: shapeProp,
  frame = 'none',
  frameColor,
  avatarUrl,
  fallback,
  btnBg,
  btnText,
  className = '',
}: ProfileAvatarProps) {
  const [imgError, setImgError] = useState(false);
  useEffect(() => { setImgError(false); }, [avatarUrl]);

  // ── Backward compat: if no shape prop and frame is a legacy shape value, derive shape from it
  const shape: AvatarShape = shapeProp ?? (LEGACY_SHAPE_FRAMES.has(frame) ? frame as AvatarShape : 'circle');
  const activeFrame: AvatarFrame = (!shapeProp && LEGACY_SHAPE_FRAMES.has(frame)) ? 'none' : frame;

  const resolvedFrameColor = (frameColor && frameColor !== '') ? frameColor : btnBg;

  // Clip-path for the current shape (undefined for circle/square)
  const clipPath = AVATAR_SHAPE_CLIP_PATHS[shape];
  const sc = shapeClassFor(shape, !!clipPath);

  const gradientBg = AVATAR_FRAME_GRADIENTS[activeFrame];
  const isSolidRing  = activeFrame === 'ring' || activeFrame === 'thick' || activeFrame === 'multi-ring';
  const isFilterFx   = ['glow','neon','soft-glow','shadow','hard-shadow'].includes(activeFrame);

  // Ring padding in px for solid-ring technique on shaped avatars
  const ringPad = activeFrame === 'thick' ? 6 : activeFrame === 'multi-ring' ? 4 : 3;

  // filter: drop-shadow() — works WITH clip-path (unlike box-shadow which gets clipped)
  const dropShadow =
    activeFrame === 'glow'        ? `drop-shadow(0 0 5px ${resolvedFrameColor}) drop-shadow(0 0 14px ${resolvedFrameColor}80)` :
    activeFrame === 'neon'        ? `drop-shadow(0 0 3px ${resolvedFrameColor}) drop-shadow(0 0 8px ${resolvedFrameColor}) drop-shadow(0 0 18px ${resolvedFrameColor}70)` :
    activeFrame === 'soft-glow'   ? `drop-shadow(0 0 14px ${resolvedFrameColor}60) drop-shadow(0 0 28px ${resolvedFrameColor}35)` :
    activeFrame === 'shadow'      ? `drop-shadow(4px 4px 0 ${resolvedFrameColor})` :
    activeFrame === 'hard-shadow' ? `drop-shadow(7px 7px 0 ${resolvedFrameColor})` :
    null;

  const content = avatarUrl && !imgError ? (
    <img src={avatarUrl} alt={fallback} className="w-full h-full object-cover" onError={() => setImgError(true)} />
  ) : (
    <span>{fallback}</span>
  );

  // The inner avatar block — always 96×96, shaped via clip-path / border-radius
  const innerBlock = (overrideClass?: string) => (
    <div
      className={`w-24 h-24 ${overrideClass ?? sc} flex items-center justify-center text-3xl font-bold overflow-hidden`}
      style={{ backgroundColor: btnBg, color: btnText, ...(clipPath ? { clipPath } : {}) }}
    >
      {content}
    </div>
  );

  // ── Gradient frame (wrap with gradient border, respects clip-path shapes)
  if (gradientBg) {
    return (
      <div
        className={`p-[3px] ${sc} ${className}`}
        style={{ background: gradientBg, ...(clipPath ? { clipPath } : {}) }}
      >
        {innerBlock()}
      </div>
    );
  }

  // ── Solid ring on clip-path shapes (box-shadow is clipped, use wrapper-padding trick)
  if (isSolidRing && clipPath) {
    return (
      <div
        className={`${sc} ${className}`}
        style={{ padding: `${ringPad}px`, background: resolvedFrameColor, clipPath }}
      >
        {innerBlock()}
      </div>
    );
  }

  // ── Glow / neon / shadow on clip-path shapes (use CSS filter: drop-shadow)
  if (isFilterFx && clipPath && dropShadow) {
    return (
      <div className={`${className}`} style={{ filter: dropShadow }}>
        {innerBlock()}
      </div>
    );
  }

  // ── Default: circle / square, or shaped with border-style effects (best-effort)
  const frameStyle = resolveFrameStyle(activeFrame, resolvedFrameColor);
  const hasShadow  = activeFrame === 'none';
  return (
    <div
      className={`w-24 h-24 ${sc} flex items-center justify-center text-3xl font-bold overflow-hidden ${hasShadow ? 'shadow-lg' : ''} ${className}`}
      style={{ backgroundColor: btnBg, color: btnText, ...(clipPath ? { clipPath } : {}), ...frameStyle }}
    >
      {content}
    </div>
  );
}
